/**
 * usePaymentRulesWizardMachine.js
 *
 * Simplified state machine for Payment Rules Wizard.
 * Uses Vue reactive system instead of full XState machine (which would be ~1000+ lines).
 * Exposes the same interface as StateMachineWizardModal expects.
 *
 * This is appropriate because:
 * - Simple linear flow (5 steps, no complex branching)
 * - No async state transitions (all data sync)
 * - Easier to maintain than full XState machine
 * - Still follows StateMachineWizardModal pattern
 */

import { ref, computed, markRaw } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '@/eventBus';
import { log } from '@/utils/DebugLogger';
import { usePaymentRules } from './usePaymentRules';
import { useFiltro } from './useFiltro';
import type { Payroll_CreatePaymentRuleInput } from '../contracts/Payroll_CreatePaymentRuleInput';

// Import step components
import Step1Franchise from '@/domains/payroll/payment-rules/wizard-steps/Step1Franchise.vue';
import Step2Employee from '@/domains/payroll/payment-rules/wizard-steps/Step2Employee.vue';
import Step3RuleType from '@/domains/payroll/payment-rules/wizard-steps/Step3RuleType.vue';
import Step4Calculation from '@/domains/payroll/payment-rules/wizard-steps/Step4Calculation.vue';
import Step5Review from '@/domains/payroll/payment-rules/wizard-steps/Step5Review.vue';

// Step component mapping
const STEP_COMPONENTS = {
  franchise: markRaw(Step1Franchise),
  employee: markRaw(Step2Employee),
  ruleType: markRaw(Step3RuleType),
  calculation: markRaw(Step4Calculation),
  review: markRaw(Step5Review)
};

/**
 * State keys in order
 */
const STEP_KEYS = ['franchise', 'employee', 'ruleType', 'calculation', 'review'];

/**
 * Step labels in Portuguese
 */
const STEP_LABELS = {
  franchise: 'Franquia',
  employee: 'Funcionário',
  ruleType: 'Tipo de Regra',
  calculation: 'Cálculo',
  review: 'Revisão'
};

/**
 * Create payment rules wizard state machine
 */
export function usePaymentRulesWizardMachine() {
  const { t } = useI18n();
  const { createRule, updateRule, deactivateRule } = usePaymentRules();
  const { selectedFranchises } = useFiltro();

  // =====================================================================
  // STATE
  // =====================================================================

  const currentStepKey = ref('franchise');
  const isLoading = ref(false);
  const error = ref(null);

  // Wizard data (form fields)
  const wizardData = ref<any>({
    franchise_id: null,
    user_id: null,
    rule_type: null,
    commission_structure: 'simple',
    brackets: [] as any[],
    amount: 0,
    period_type: 'monthly',
    description: '',
    meta: null
  });

  // Edit mode
  const isEditMode = ref(false);
  const isReadonlyMode = ref(false); // When true, viewing existing rule (no edits allowed)
  const existingRule = ref(null as any);
  const existingRules = ref([] as any[]);

  // Result
  const saveResult = ref({
    success: false,
    error: null
  });

  // =====================================================================
  // COMPUTED - Machine Interface (for StateMachineWizardModal)
  // =====================================================================

  /**
   * Current step index (0-based)
   */
  const currentStepIndex = computed(() => {
    return STEP_KEYS.indexOf(currentStepKey.value);
  });

  /**
   * Current state name (for compatibility with StateMachineWizardModal)
   * Returns 'finished' when saved successfully, otherwise returns current step key (including 'review')
   */
  const currentState = computed(() => {
    if (saveResult.value.success) {
      return 'finished';
    }
    return currentStepKey.value;
  });

  /**
   * Visible steps array
   * In readonly mode, hide the review step (step 5) to prevent navigation
   */
  const visibleSteps = computed(() => {
    return STEP_KEYS.map((key: any) => ({
      key,
      title: t((STEP_LABELS as any)[key])
    }));
  });

  /**
   * Current step component
   */
  const currentStepComponent = computed(() => {
    return (STEP_COMPONENTS as any)[currentStepKey.value] || null;
  });

  /**
   * Can proceed to next step
   */
  const canProceed = computed(() => {
    // Block all navigation in readonly mode
    if (isReadonlyMode.value) {
      return false;
    }

    switch (currentStepKey.value) {
      case 'franchise':
        return !!wizardData.value.franchise_id;
      case 'employee':
        return !!wizardData.value.user_id;
      case 'ruleType':
        return !!wizardData.value.rule_type;
      case 'calculation':
        return true; // Validation handled in step
      case 'review':
        return true;
      default:
        return false;
    }
  });

  /**
   * Can go back
   */
  const canGoBack = computed(() => {
    return currentStepIndex.value > 0;
  });

  // =====================================================================
  // ACTIONS
  // =====================================================================

  /**
   * Go to next step
   */
  const next = () => {
    log('usePaymentRulesWizardMachine', 'next', 'Moving to next step', {
      from: currentStepKey.value,
      isReadonly: isReadonlyMode.value
    });

    // Block navigation if in readonly mode (viewing existing rule)
    if (isReadonlyMode.value) {
      log('usePaymentRulesWizardMachine', 'next', 'Blocked - readonly mode');
      return;
    }

    const currentIndex = currentStepIndex.value;
    if (currentIndex < STEP_KEYS.length - 1) {
      currentStepKey.value = STEP_KEYS[currentIndex + 1];
    }
  };

  /**
   * Go back to previous step
   */
  const back = () => {
    log('usePaymentRulesWizardMachine', 'back', 'Moving to previous step', {
      from: currentStepKey.value
    });

    const currentIndex = currentStepIndex.value;
    if (currentIndex > 0) {
      currentStepKey.value = STEP_KEYS[currentIndex - 1];
    }
  };

  /**
   * Go to specific step (for step click support)
   */
  const goToStep = (stepIndex: any) => {
    log('usePaymentRulesWizardMachine', 'goToStep', 'Going to step', {
      to: stepIndex
    });

    if (stepIndex >= 0 && stepIndex < STEP_KEYS.length) {
      currentStepKey.value = STEP_KEYS[stepIndex];
    }
  };

  /**
   * Confirm and save
   */
  const confirm = async () => {
    const timestamp = new Date().toISOString();
    const callId = Math.random().toString(36).substring(7);

    log('usePaymentRulesWizardMachine', 'confirm', `START [${timestamp}][${callId}]`, {
      isEditMode: isEditMode.value,
      isReadonlyMode: isReadonlyMode.value
    });

    // Block save if in readonly mode (viewing existing rule)
    if (isReadonlyMode.value) {
      log('usePaymentRulesWizardMachine', 'confirm', `BLOCKED - readonly mode [${timestamp}][${callId}]`);
      error.value = t('Regras de pagamento não podem ser editadas. Desative esta regra e crie uma nova.') as any;
      saveResult.value = { success: false, error: error.value };
      return;
    }

    isLoading.value = true;
    error.value = null;

    log('usePaymentRulesWizardMachine', 'confirm', `submitData [${timestamp}][${callId}]`, {
      franchise_id: wizardData.value.franchise_id,
      user_id: wizardData.value.user_id,
      rule_type: wizardData.value.rule_type,
      amount: wizardData.value.amount,
      description: wizardData.value.description,
      meta: wizardData.value.meta
    });

    try {
      // Prepare data for API — meta is serialized as a JSON string per the contract
      const metaObj: Record<string, unknown> = {};

      // For structured commissions, set amount to first bracket's rate
      let amount = wizardData.value.amount;
      if (wizardData.value.commission_structure !== 'simple' && wizardData.value.rule_type?.includes('commission')) {
        amount = (wizardData.value.brackets as any)[0]?.rate || 0.01;
        metaObj.commission_structure = {
          type: wizardData.value.commission_structure,
          brackets: wizardData.value.brackets
        };
      }

      // For fixed salary with period type
      if (wizardData.value.rule_type === 'fixed') {
        metaObj.period_type = wizardData.value.period_type;
      }

      const submitData: Payroll_CreatePaymentRuleInput = {
        franchise_id: wizardData.value.franchise_id,
        user_id: wizardData.value.user_id,
        rule_type: wizardData.value.rule_type,
        amount,
        description: wizardData.value.description,
        meta: Object.keys(metaObj).length > 0 ? JSON.stringify(metaObj) : undefined
      };

      log('usePaymentRulesWizardMachine', 'confirm', `Calling createRule [${timestamp}][${callId}]`);

      // Save rule
      if (isEditMode.value) {
        await updateRule(existingRule.value!.id, {
          amount: submitData.amount,
          description: submitData.description,
          meta: submitData.meta
        });
      } else {
        const newRule = await createRule(submitData);
        log('usePaymentRulesWizardMachine', 'confirm', `createRule returned [${timestamp}][${callId}]`, newRule);
        log('usePaymentRulesWizardMachine', 'confirm', 'Rule created successfully', newRule!.id);
      }

      saveResult.value = { success: true, error: null };
      log('usePaymentRulesWizardMachine', 'confirm', `emit payment-rule.saved event [${timestamp}][${callId}]`);

      // Emit event for parent to refresh
      eventBus.emit('payment-rule.saved');
    } catch (err) {
      log('usePaymentRulesWizardMachine', 'confirm', 'ERROR saving payment rule', err);
      error.value = err.message || t('Erro ao salvar regra de pagamento');
      saveResult.value = { success: false, error: error.value };
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Cancel wizard
   */
  const cancel = () => {
    log('usePaymentRulesWizardMachine', 'cancel', 'Cancelling wizard');
    reset();
  };

  /**
   * Reset wizard to initial state
   */
  const reset = () => {
    currentStepKey.value = 'franchise';
    isLoading.value = false;
    error.value = null;
    saveResult.value = { success: false, error: null };
    isEditMode.value = false;
    isReadonlyMode.value = false;
    existingRule.value = null;
    existingRules.value = [];
    wizardData.value = {
      franchise_id: null,
      user_id: null,
      rule_type: null,
      commission_structure: 'simple',
      brackets: [],
      amount: 0,
      period_type: 'monthly',
      description: '',
      meta: null
    };
  };

  /**
   * Start wizard with data (for edit mode or resume)
   */
  const start = (data: any = {}) => {
    log('usePaymentRulesWizardMachine', 'start', 'Starting wizard', data);

    reset();

    if (data.rule) {
      // Edit mode - viewing existing rule
      isEditMode.value = true;
      isReadonlyMode.value = true; // Always readonly when editing existing rules
      existingRule.value = data.rule;
      existingRules.value = data.existingRules || [];

      // Populate form with existing data
      (wizardData.value as any) = {
        franchise_id: parseInt((data.rule as any).franchise_id),
        user_id: String((data.rule as any).user_id),
        rule_type: (data.rule as any).rule_type,
        commission_structure: 'simple',
        brackets: [],
        amount: parseFloat((data.rule as any).amount),
        period_type: 'monthly',
        description: (data.rule as any).description || '',
        meta: null
      };

      // Parse meta field for commission structure or period_type
      if ((data.rule as any).meta) {
        try {
          const meta = typeof (data.rule as any).meta === 'string' ? JSON.parse((data.rule as any).meta) : (data.rule as any).meta;
          if ((data.rule as any).rule_type === 'fixed' && meta?.period_type) {
            (wizardData.value as any).period_type = meta.period_type;
          }
          if (meta?.commission_structure) {
            (wizardData.value as any).commission_structure = meta.commission_structure.type || 'simple';
            if (meta.commission_structure.brackets) {
              (wizardData.value as any).brackets = meta.commission_structure.brackets.map((b: any) => ({
                min: b.min,
                max: b.max,
                rate: b.rate,
                ratePercent: Math.round(b.rate * 100 * 100) / 100
              }));
            }
          }
        } catch (e) {
          log('usePaymentRulesWizardMachine', 'start', 'Error parsing meta field', e);
        }
      }

      // Go to review step directly in readonly mode (show full rule + actions)
      currentStepKey.value = 'review';
    } else {
      // New mode - start from first step
      currentStepKey.value = 'franchise';
    }
  };

  /**
   * Get label for next button based on step
   */
  const getNextLabelForStep = (index: any) => {
    const stepKey = STEP_KEYS[index];
    if (stepKey === 'review') {
      return t('Confirmar');
    }
    return t('Próximo');
  };

  // =====================================================================
  // PUBLIC API
  // =====================================================================

  return {
    // State
    currentStepIndex,
    currentState,
    visibleSteps,
    currentStepComponent,
    isLoading,
    canProceed,
    canGoBack,
    error,
    wizardData,
    isEditMode,
    isReadonlyMode,
    existingRule,
    existingRules,
    saveResult,

    // Actions
    next,
    back,
    confirm,
    cancel,
    reset,
    start,
    goToStep,
    getNextLabelForStep
  };
}
